import React, { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  TextField,
  Button,
  Grid,
  FormControl,
  FormLabel,
  Slider,
  Avatar,
  Stack,
  Box,
  Typography,
  DialogContent
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PhoneInput } from 'react-international-phone';
import Cropper from 'react-easy-crop';
import MainCard from 'components/MainCard';
import BackButton from 'components/Button/BackButton';
import { getUserByID } from 'services/users/getUserByID';
import { editUserProfile } from 'services/users/editUserProfile';
import 'react-international-phone/style.css';
import UploadImage from 'utils/UploadImage';
import getCroppedImg from 'utils/getCroppedImg';
import { MenuItem, Select, InputLabel } from '@mui/material';
import { EditOutlined, LockOutlined } from '@ant-design/icons';
import { changePassword } from 'services/users/changePassword';

//

const EditProfile = () => {
  const roleId = localStorage.getItem('roleid');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: userData } = useQuery({
    queryKey: ['getUserByID', roleId],
    queryFn: () => getUserByID({ roleid: Number(roleId) }),
    enabled: !!roleId
  });

  console.log('userData', userData);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    emailAddress: '',
    mobileNumber: '',
    avatar: '',
    studioName: '',
    subscriptionPlan: ''
  });

  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropDialogOpen, setIsCropDialogOpen] = useState(false);
  const imageFileRef = useRef(null);

  const editMutation = useMutation({
    mutationFn: editUserProfile,
    onSuccess: (data) => {
      if (data?.status) {
        queryClient.invalidateQueries({ queryKey: ['getUserByID'] });
        toast.success(data?.message || 'Profile updated successfully');
        navigate('/dashboard');
      } else {
        toast.error(data?.message || 'Failed to update profile');
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'An error occurred');
    }
  });

  const changePasswordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: (data) => {
      if (data?.status) {
        toast.success(data?.message || 'Password changed successfully');
        setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        toast.error(data?.message || 'Failed to change password');
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'An error occurred');
    }
  });

  const formatDobToInput = (isoDate) => {
    if (!isoDate) return '';
    return new Date(isoDate).toISOString().split('T')[0]; // "YYYY-MM-DD"
  };

  // Converts 'yyyy-mm-dd' -> 'dd-mm-yyyy' for backend
  const formatDobToSubmit = (dob) => {
    if (!dob || !dob.includes('-')) return '';
    const [yyyy, mm, dd] = dob.split('-');
    return `${dd}-${mm}-${yyyy}`;
  };

  useEffect(() => {
    if (userData) {
      setFormData({
        firstName: userData.firstname || '',
        lastName: userData.lastname || '',
        dateOfBirth: roleId === '2' ? formatDobToInput(userData.dateOfBirth || '') : '',
        emailAddress: userData.emailaddress || '',
        mobileNumber: userData.mobilenumber || '',
        avatar: userData.avatar || '',
        studioName: roleId === '3' ? userData.studioName || '' : '',
        subscriptionPlan: roleId === '3' ? userData.subscriptionPlan || '' : ''
      });
    }
  }, [userData, roleId]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should not exceed 5MB');
      if (imageFileRef.current) imageFileRef.current.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setCropImage(reader.result);
      setIsCropDialogOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropSave = async () => {
    try {
      if (!cropImage || !croppedAreaPixels) return;
      const croppedImage = await getCroppedImg(cropImage, croppedAreaPixels);
      const imageUrl = await UploadImage({ srcData: croppedImage, folderName: 'user-profile' });
      setFormData((prev) => ({ ...prev, avatar: imageUrl }));
      setIsCropDialogOpen(false);
      toast.success('Profile image uploaded successfully');
    } catch (error) {
      toast.error('Error processing image');
    }
  };

  const handleCropComplete = (_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (phone) => {
    setFormData((prev) => ({ ...prev, mobileNumber: phone }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.emailAddress || !formData.mobileNumber) {
      toast.error('Please fill in all required fields');
      return;
    }

    const dataToSend = {
      roleid: Number(roleId),
      firstName: formData.firstName,
      lastName: formData.lastName,
      emailAddress: formData.emailAddress,
      mobileNumber: formData.mobileNumber,
      avatar: formData.avatar
    };
    if (roleId === '2') {
      dataToSend.dateOfBirth = formatDobToSubmit(formData.dateOfBirth);
    }
    if (roleId === '3') {
      dataToSend.studioName = formData.studioName;
      dataToSend.subscriptionPlan = formData.subscriptionPlan;
    }
    editMutation.mutate(dataToSend);
  };
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async () => {
    if (!passwordData.oldPassword || !passwordData.newPassword) {
      toast.error('Both old and new passwords are required');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    changePasswordMutation.mutate({
      oldPassword: passwordData.oldPassword,
      newPassword: passwordData.newPassword
    });
  };

  return (
    <Box sx={{ maxWidth: '1200px', margin: '0 auto', padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, mb: 3 }}>
        <BackButton />
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" color="secondary" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={editMutation.isPending}>
            {editMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </Box>
      <MainCard title="Edit Profile">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center">
              {roleId !== '2' && (
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="center">
                    <FormLabel htmlFor="change-avatar" sx={{ position: 'relative', cursor: 'pointer' }}>
                      <Avatar src={formData.avatar} sx={{ width: 124, height: 124 }} />
                      <Box
                        className="overlay"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          bgcolor: 'rgba(0, 0, 0, 0.5)',
                          opacity: 0,
                          transition: 'opacity 0.3s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&:hover': { opacity: 1 }
                        }}
                      >
                        <Stack alignItems="center">
                          <EditOutlined style={{ fontSize: '1.8rem', color: 'white' }} />
                          <Typography color="white">Edit</Typography>
                        </Stack>
                      </Box>
                      <TextField
                        type="file"
                        accept="image/*"
                        id="change-avatar"
                        sx={{ display: 'none' }}
                        inputRef={imageFileRef}
                        onChange={handleFileChange}
                      />
                    </FormLabel>
                  </Box>
                </Grid>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField name="firstName" label="First Name" fullWidth value={formData.firstName} onChange={handleChange} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField name="lastName" label="Last Name" fullWidth value={formData.lastName} onChange={handleChange} required />
          </Grid>
          {roleId === '2' && (
            <Grid item xs={12} sm={6}>
              <TextField
                name="dateOfBirth"
                label="Date of Birth"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formData.dateOfBirth}
                onChange={handleChange}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField name="emailAddress" label="Email Address" fullWidth value={formData.emailAddress} onChange={handleChange} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              {/* <FormLabel>Mobile Number</FormLabel> */}
              <PhoneInput
                value={formData.mobileNumber}
                defaultCountry="ae"
                onChange={handlePhoneChange}
                style={{ width: '100%' }}
                inputStyle={{ width: '100%' }}
              />
            </FormControl>
          </Grid>
          {roleId === '3' && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField name="studioName" label="Studio Name" fullWidth value={formData.studioName} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="subscription-label">Subscription Plan</InputLabel>
                  <Select
                    labelId="subscription-label"
                    id="subscriptionPlan"
                    name="subscriptionPlan"
                    value={formData.subscriptionPlan}
                    label="Subscription Plan"
                    onChange={handleChange}
                  >
                    <MenuItem value="free">Free</MenuItem>
                    <MenuItem value="pro">Pro</MenuItem>
                    <MenuItem value="studio">Studio</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>
      </MainCard>

      <Box sx={{ mt: 4 }}>
        <MainCard title="Change Password">
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField 
                name="oldPassword" 
                label="Current Password" 
                type="password" 
                fullWidth 
                value={passwordData.oldPassword} 
                onChange={handlePasswordChange} 
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                name="newPassword" 
                label="New Password" 
                type="password" 
                fullWidth 
                value={passwordData.newPassword} 
                onChange={handlePasswordChange} 
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                name="confirmPassword" 
                label="Confirm New Password" 
                type="password" 
                fullWidth 
                value={passwordData.confirmPassword} 
                onChange={handlePasswordChange} 
              />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                color="error" 
                startIcon={<LockOutlined />} 
                onClick={handlePasswordSubmit}
                disabled={changePasswordMutation.isPending}
              >
                {changePasswordMutation.isPending ? 'Updating...' : 'Change Password'}
              </Button>
            </Grid>
          </Grid>
        </MainCard>
      </Box>
      <Dialog open={isCropDialogOpen} onClose={() => setIsCropDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Crop Profile Image</DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative', width: '100%', height: 400, bgcolor: '#333' }}>
            <Cropper
              image={cropImage}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          </Box>
          <Slider value={zoom} min={1} max={3} step={0.1} onChange={(_, value) => setZoom(value)} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCropDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleCropSave} color="primary" disabled={!croppedAreaPixels}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditProfile;
